import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

// Calls the AI writing-assist routes (proofread/rewrite/generate) hosted by the
// `ai.dnaofsafety.net`. Kept separate from BackendCallService since it targets
// a different backend (not a Blueprint microservice) with a different contract.
// The Blueprint bearer token is attached automatically by the app-wide
// AuthInterceptor — this service only needs to send which Blueprint environment
// to verify the token against.

// Keys must match app/api/ai-writing/helpers.ts's FIELD_CONTEXTS map in the
// root Next.js repo — that's what actually drives the prompt server-side.
export type AiFieldContextType =
  | 'roadmap-question'
  | 'roadmap-notes'
  | 'project-description'
  | 'service-description'
  | 'report-description'
  | 'hazard-description'
  | 'control-measure-description'
  | 'plr-description'
  | 'hazard-measure-library-title'
  | 'machine-description'
  | 'machine-limit-description';

export interface AiFieldContext {
  type: AiFieldContextType;
}

@Injectable({
  providedIn: 'root',
})
export class AiWritingService {
  constructor(private http: HttpClient) {}

  // `instruction` is required for mode 'adjust' and ignored by the other two —
  // it is the consultant's own words describing how they want the text changed.
  async transform(
    mode: 'proofread' | 'rewrite' | 'adjust',
    text: string,
    fieldContext: AiFieldContext,
    instruction?: string,
  ): Promise<string> {
    const res: any = await this.http
      .post(`${environment.aiWritingBaseUrl}/api/ai-writing/transform`, {
        mode,
        text,
        fieldContext,
        ...(mode === 'adjust' ? { instruction } : {}),
        apiUrl: environment.tokenEndpoint,
      })
      .toPromise();
    return res.result;
  }

  async generate(prompt: string, fieldContext: AiFieldContext): Promise<string> {
    const res: any = await this.http
      .post(`${environment.aiWritingBaseUrl}/api/ai-writing/generate`, {
        prompt,
        fieldContext,
        apiUrl: environment.tokenEndpoint,
      })
      .toPromise();
    return res.result;
  }
}
